import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useGetTabListOptions } from '~hooks/queries/vessels/data/use-get-tab-list'
import type { GetVesselsResItem } from '~hooks/queries/vessels/use-get-vessels'
import { constructTramosCode } from '~pages/pages-behind-login/technical-management/components/vessels-select'

export const VESSEL_TABS_BATCH_SIZE = 10

export function useVesselTourLink({
  vessels,
  pool,
  enabled,
  type,
  requiredPaths,
}: {
  vessels: GetVesselsResItem[] | undefined
  pool: string
  enabled: boolean
  type: 'operations' | 'financials'
  requiredPaths: string[]
}) {
  const queryClient = useQueryClient()
  const tabOptions = useGetTabListOptions()

  return useQuery({
    queryKey: [
      'guided-tour-vessel',
      type,
      requiredPaths,
      pool,
      vessels?.map(({ vesselId, vesselCode, pool }) => ({
        vesselId,
        vesselCode,
        pool,
      })),
    ],
    enabled: enabled && !!vessels,
    retry: false,
    staleTime: Infinity,
    queryFn: async () => {
      const candidates = vessels ?? []
      const isSelectedPool = (vessel: GetVesselsResItem) =>
        vessel.pool.toLowerCase() === pool.toLowerCase()
      const ordered = [
        ...candidates.filter(isSelectedPool),
        ...candidates.filter((vessel) => !isSelectedPool(vessel)),
      ]

      const eligible = ordered.filter(
        (vessel) => type !== 'financials' || vessel.vesselCode,
      )

      // Checking one vessel at a time can take minutes when few vessels have
      // the required tabs, so check in parallel batches while keeping order.
      for (
        let start = 0;
        start < eligible.length;
        start += VESSEL_TABS_BATCH_SIZE
      ) {
        const batch = eligible.slice(start, start + VESSEL_TABS_BATCH_SIZE)
        const batchTabs = await Promise.all(
          batch.map((vessel) =>
            queryClient.fetchQuery(
              tabOptions({ vesselId: vessel.vesselId, type }),
            ),
          ),
        )
        const matchIndex = batchTabs.findIndex(
          (tabs) =>
            tabs.length > 0 &&
            requiredPaths.every((path) =>
              tabs.some((tab) => tab.path === path),
            ),
        )
        if (matchIndex === -1) continue

        const vessel = batch[matchIndex]
        const firstPath = requiredPaths[0] ?? batchTabs[matchIndex][0].path
        if (type === 'operations') {
          return `/fleet/vessels/${encodeURIComponent(vessel.vesselId)}/${firstPath}`
        }
        const code = encodeURIComponent(constructTramosCode(vessel.vesselCode))
        const params = new URLSearchParams({ vesselId: vessel.vesselId })
        return `/financials/vessels/${code}/${firstPath}?${params}`
      }
      return null
    },
  })
}
